'use client';

/**
 * `<InboxDropdown>` — выпадающий preview-список последних N сообщений
 * (REQ-INB-002, INB §3). Контракт inbox §C.8.2. Рендерится внутри `<InboxBell>`
 * как `Popover.Dropdown`.
 *
 * Содержимое:
 * - Шапка: «Входящие» + счётчик «N новых» (если `unread_count > 0`).
 * - Список: последние `DROPDOWN_PREVIEW_COUNT` (5) сообщений из `messages[]`
 *   (`creation DESC`); рендер через `<InboxPreviewCard>`.
 * - Empty-state: «Нет новых уведомлений»; кнопка mark-all-read скрыта.
 * - Подвал: «Отметить все прочитанными» (если `unread_count > 0`) + «Все уведомления».
 *
 * Свежесть (INB §3.3): на открытие — `refetch({ unreadOnly: false })` для полного
 * списка (чтобы не показывать устаревший снимок).
 */

import { useEffect } from 'react';
import { Anchor, Button, Divider, Group, Stack, Text } from '@mantine/core';

import { useInbox } from '@/lib/inbox/useInbox';
import { InboxPreviewCard } from './InboxPreviewCard';

export const DROPDOWN_PREVIEW_COUNT = 5;

interface InboxDropdownProps {
  /** Вызывается после любой активации карточки (mark-read + переход/раскрытие),
   *  чтобы родительский `<InboxBell>` закрыл Popover. */
  onClose: () => void;
}

export function InboxDropdown({ onClose }: InboxDropdownProps) {
  const { messages, unread_count, markRead, markAllRead, refetch } = useInbox();

  // На открытие — догрузить полный список (INB §3.3, чтобы не показывать устаревший
  // снимок от badge-fetch'а, который был только непрочитанный).
  useEffect(() => {
    void refetch({ unreadOnly: false, force: true });
  }, [refetch]);

  const preview = messages.slice(0, DROPDOWN_PREVIEW_COUNT);
  const isEmpty = messages.length === 0;

  const handleActivate = async (message: Parameters<typeof markRead>[0]) => {
    try {
      await markRead(message);
    } catch {
      // toast handled by caller (Header) через notifications; здесь тихо
    }
    onClose();
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead();
    } catch {
      // тихо; toast caller
    }
    onClose();
  };

  return (
    <Stack gap={0} style={{ minWidth: 340, maxWidth: 380 }}>
      {/* Шапка */}
      <Group justify="space-between" px="sm" py="xs">
        <Text fw={700} size="sm">
          Входящие
        </Text>
        {unread_count > 0 ? (
          <Text size="xs" c="dimmed">
            {unread_count} новых
          </Text>
        ) : null}
      </Group>
      <Divider />

      {/* Список / empty-state */}
      {isEmpty ? (
        <Text size="sm" c="dimmed" px="sm" py="lg" ta="center">
          Нет новых уведомлений
        </Text>
      ) : (
        <Stack gap={0}>
          {preview.map((m) => (
            <InboxPreviewCard key={m.id} message={m} onActivate={handleActivate} />
          ))}
        </Stack>
      )}

      {/* Подвал */}
      <Divider />
      <Group justify="space-between" px="sm" py="xs" gap="sm">
        {unread_count > 0 ? (
          <Button
            variant="subtle"
            size="xs"
            onClick={handleMarkAll}
            aria-label="Отметить все прочитанными"
          >
            Отметить все прочитанными
          </Button>
        ) : (
          <span />
        )}
        <Anchor
          href="/inbox"
          size="xs"
          fw={500}
          underline="always"
          onClick={onClose}
        >
          Все уведомления
        </Anchor>
      </Group>
    </Stack>
  );
}
